/**
 * Gráficos em SVG próprio (sem biblioteca): rosca de status, barras horizontais e verticais, colunas agrupadas e linhas (curva S).
 * Regras (docs/tecnico/interface.md): marcas finas com ponta arredondada, intervalo de 2 px entre fatias, legenda com
 * rótulo e número (a cor nunca carrega o sentido sozinha), grade discreta e dica ao passar o mouse ou focar.
 * Os textos usam as cores de texto; as cores de série ficam só nas marcas.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { axisLeft, niceTicks } from '../lib/axis';

interface Tip { x: number; y: number; content: ReactNode }

function Tooltip({ tip }: { tip: Tip | null }) {
  if (!tip) return null;
  return (
    <div role="tooltip" className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-[calc(100%+10px)] whitespace-nowrap rounded-md border border-line bg-panel px-2.5 py-1.5 text-xs shadow-lg"
      style={{ left: tip.x, top: tip.y }}>
      {tip.content}
    </div>
  );
}

export interface Slice { key: string; label: string; value: number; color: string; icon?: ReactNode }

/** Rosca (parte do todo, poucas categorias) com o total no centro e legenda com rótulo, número e %. */
export function Donut({ slices, size = 200, total, centerLabel, onSelect, selected }: {
  slices: Slice[]; size?: number; total?: number; centerLabel?: string; onSelect?: (key: string | null) => void; selected?: string | null;
}) {
  const [tip, setTip] = useState<Tip | null>(null);
  const sum = slices.reduce((s, x) => s + x.value, 0);
  const r = size / 2 - 6;
  const inner = r * 0.64;
  const cx = size / 2;
  const gap = 2 / r; // 2 px de intervalo entre fatias, em radianos
  let a0 = -Math.PI / 2;
  const arcs = slices.filter((s) => s.value > 0).map((s) => {
    const span = (s.value / (sum || 1)) * Math.PI * 2;
    const start = a0 + (slices.filter((x) => x.value > 0).length > 1 ? gap / 2 : 0);
    const end = a0 + span - (slices.filter((x) => x.value > 0).length > 1 ? gap / 2 : 0);
    a0 += span;
    const large = end - start > Math.PI ? 1 : 0;
    const p = (rad: number, rr: number) => `${cx + rr * Math.cos(rad)} ${cx + rr * Math.sin(rad)}`;
    const d = span >= Math.PI * 2 - 1e-6
      ? `M ${cx - r} ${cx} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0 M ${cx - inner} ${cx} a ${inner} ${inner} 0 1 1 ${2 * inner} 0 a ${inner} ${inner} 0 1 1 ${-2 * inner} 0`
      : `M ${p(start, r)} A ${r} ${r} 0 ${large} 1 ${p(end, r)} L ${p(end, inner)} A ${inner} ${inner} 0 ${large} 0 ${p(start, inner)} Z`;
    const mid = (start + end) / 2;
    return { ...s, d, tx: cx + ((r + inner) / 2) * Math.cos(mid), ty: cx + ((r + inner) / 2) * Math.sin(mid) };
  });
  return (
    <div className="flex flex-wrap items-center gap-5">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Distribuição por status: ${slices.map((s) => `${s.label} ${s.value}`).join(', ')}`}>
          {sum === 0 ? <circle cx={cx} cy={cx} r={(r + inner) / 2} fill="none" stroke="var(--grid)" strokeWidth={r - inner} /> : null}
          {arcs.map((a) => (
            <path key={a.key} d={a.d} fill={a.color} fillRule="evenodd" tabIndex={0} role="button" aria-label={`${a.label}: ${a.value}`}
              className={clsx('cursor-pointer outline-none transition-opacity', selected && selected !== a.key && 'opacity-35')}
              onPointerMove={() => setTip({ x: a.tx, y: a.ty, content: <><b className="text-sm">{a.value}</b> <span className="text-muted">{a.label} · {Math.round((a.value / sum) * 100)}%</span></> })}
              onPointerLeave={() => setTip(null)} onFocus={() => setTip({ x: a.tx, y: a.ty, content: <><b>{a.value}</b> {a.label}</> })} onBlur={() => setTip(null)}
              onClick={() => onSelect?.(selected === a.key ? null : a.key)} />
          ))}
        </svg>
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div>
            <div className="text-3xl font-semibold leading-none">{total ?? sum}</div>
            {centerLabel ? <div className="mt-1 text-[11px] text-muted">{centerLabel}</div> : null}
          </div>
        </div>
        <Tooltip tip={tip} />
      </div>
      <ul className="min-w-40 flex-1 space-y-1.5 text-sm">
        {slices.map((s) => (
          <li key={s.key}>
            <button type="button" onClick={() => onSelect?.(selected === s.key ? null : s.key)}
              className={clsx('flex w-full items-center gap-2 rounded-md px-1.5 py-0.5 text-left hover:bg-panel-2', selected && selected !== s.key && 'opacity-50')}>
              {s.icon ?? <span className="size-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />}
              <span className="flex-1 truncate text-fg">{s.label}</span>
              <span className="num font-semibold">{s.value}</span>
              <span className="num w-9 text-right text-xs text-muted">{sum ? Math.round((s.value / sum) * 100) : 0}%</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Barras horizontais (uma série): rótulo à esquerda, valor na ponta. */
export function HBars({ data, color = 'var(--teal)', max, onSelect, selected, valueFmt = (v: number) => String(v) }: {
  data: { label: string; value: number; key?: string; lead?: ReactNode; sub?: string }[]; color?: string; max?: number; onSelect?: (k: string | null) => void; selected?: string | null;
  valueFmt?: (v: number) => string;
}) {
  const m = max ?? Math.max(1, ...data.map((d) => d.value));
  return (
    <ul className="space-y-2.5" role="list">
      {data.map((d) => {
        const k = d.key ?? d.label;
        return (
          <li key={k}>
            <button type="button" onClick={() => onSelect?.(selected === k ? null : k)} title={`${d.label}: ${valueFmt(d.value)}`}
              className={clsx('group grid w-full grid-cols-[minmax(110px,38%)_1fr] items-center gap-3 rounded-md text-left', selected && selected !== k && 'opacity-45')}>
              <span className="flex min-w-0 items-center gap-2">
                {d.lead}
                <span className="min-w-0"><span className="block truncate text-xs text-fg/85 group-hover:text-fg">{d.label}</span>{d.sub ? <span className="block truncate text-[10px] text-muted">{d.sub}</span> : null}</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="h-3.5 rounded-r-[4px] transition-[width]" style={{ width: `${Math.max(1.5, (d.value / m) * 80)}%`, background: color }} />
                <span className="num text-xs font-semibold text-fg">{valueFmt(d.value)}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Colunas (uma série): valor no topo, rótulo embaixo; rótulos longos quebram em duas linhas. */
export function Columns({ data, color = 'var(--accent)', height = 180, onSelect, selected }: {
  data: { label: string; value: number; key?: string }[]; color?: string; height?: number; onSelect?: (k: string | null) => void; selected?: string | null;
}) {
  const m = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex items-end gap-3 overflow-x-auto pb-1" style={{ height: height + 48 }}>
      {data.map((d) => {
        const k = d.key ?? d.label;
        const h = Math.max(3, (d.value / m) * height);
        return (
          <button key={k} type="button" onClick={() => onSelect?.(selected === k ? null : k)} title={`${d.label}: ${d.value}`}
            className={clsx('flex min-w-14 flex-1 flex-col items-center justify-end gap-1', selected && selected !== k && 'opacity-45')}>
            <span className="num text-xs font-semibold">{d.value}</span>
            <span className="w-full max-w-6 rounded-t-[4px]" style={{ height: h, background: color }} />
            <span className="line-clamp-2 h-8 text-center text-[11px] leading-tight text-muted">{d.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export interface LineSeries { key: string; label: string; color: string; points: { x: string; y: number }[]; dashed?: boolean }

/**
 * Linhas no tempo (ex.: curva S planejado × realizado), com linha-guia que acompanha o mouse e uma dica com todas as séries.
 * O eixo vai de 0 a um teto redondo (yMax é o teto mínimo) e a margem esquerda cabe o maior rótulo; `yFmt` formata o eixo
 * (compacto, ex.: "R$ 2,5 mi") e `tipFmt` a dica (o valor exato; sem ele, o mesmo do eixo).
 */
export function LineChart({ series, height = 220, yMax, yFmt = (v: number) => String(Math.round(v)), tipFmt = yFmt, xFmt = (x: string) => x }: {
  series: LineSeries[]; height?: number; yMax?: number; yFmt?: (v: number) => string; tipFmt?: (v: number) => string; xFmt?: (x: string) => string;
}) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const xs = [...new Set(series.flatMap((s) => s.points.map((p) => p.x)))].sort();
  const ticks = niceTicks(yMax ?? Math.max(1, ...series.flatMap((s) => s.points.map((p) => p.y))));
  const max = ticks.at(-1)!;
  const W = 640;
  const pad = { l: axisLeft(ticks.map(yFmt)), r: 16, t: 12, b: 26 };
  const x = (i: number) => pad.l + (xs.length <= 1 ? 0 : (i / (xs.length - 1)) * (W - pad.l - pad.r));
  const y = (v: number) => pad.t + (1 - v / max) * (height - pad.t - pad.b);
  if (!xs.length) return <div className="grid h-32 place-items-center text-sm text-muted">Sem dados para o gráfico.</div>;
  const labelEvery = Math.ceil(xs.length / 6);
  return (
    <div className="relative">
      <div className="mb-2 flex flex-wrap gap-4 text-xs text-muted">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <svg width="18" height="6" aria-hidden><line x1="0" y1="3" x2="18" y2="3" stroke={s.color} strokeWidth="2" strokeDasharray={s.dashed ? '4 3' : undefined} /></svg>
            {s.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${height}`} className="w-full" role="img" aria-labelledby={`${id}-t`}
        onPointerMove={(e) => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * W;
          let best = 0;
          xs.forEach((_, i) => { if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i; });
          setHover(best);
        }} onPointerLeave={() => setHover(null)}>
        <title id={`${id}-t`}>{series.map((s) => s.label).join(' × ')}</title>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="var(--grid)" strokeWidth="1" />
            <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill="var(--muted)" className="num">{yFmt(t)}</text>
          </g>
        ))}
        {xs.map((v, i) => (i % labelEvery === 0 || i === xs.length - 1
          ? <text key={v} x={x(i)} y={height - 8} textAnchor="middle" fontSize="10" fill="var(--muted)">{xFmt(v)}</text> : null))}
        {series.map((s) => {
          const pts = s.points.map((p) => [x(xs.indexOf(p.x)), y(p.y)] as const);
          return (
            <g key={s.key}>
              <polyline points={pts.map((p) => p.join(',')).join(' ')} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={s.dashed ? '5 4' : undefined} />
              {pts.length ? <circle cx={pts.at(-1)![0]} cy={pts.at(-1)![1]} r="4" fill={s.color} stroke="var(--panel)" strokeWidth="2" /> : null}
            </g>
          );
        })}
        {hover != null ? <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={height - pad.b} stroke="var(--axis)" strokeWidth="1" /> : null}
      </svg>
      {hover != null ? (
        <div className="pointer-events-none absolute right-2 top-7 rounded-md border border-line bg-panel px-2.5 py-1.5 text-xs shadow-lg">
          <div className="mb-1 text-muted">{xFmt(xs[hover]!)}</div>
          {series.map((s) => {
            const p = s.points.find((q) => q.x === xs[hover]);
            return p ? (
              <div key={s.key} className="flex items-center gap-2">
                <svg width="12" height="4" aria-hidden><line x1="0" y1="2" x2="12" y2="2" stroke={s.color} strokeWidth="2" /></svg>
                <b className="num">{tipFmt(p.y)}</b><span className="text-muted">{s.label}</span>
              </div>
            ) : null;
          })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Barra empilhada única (parte do todo): segmentos com intervalo de 2 px, pontas arredondadas, dica no mouse/foco.
 * A legenda (rótulo + número) vem logo abaixo; a cor nunca carrega o sentido sozinha.
 */
export function StackedBar({ parts, height = 14, onSelect, selected }: {
  parts: { key: string; label: string; value: number; color: string; icon?: ReactNode }[]; height?: number; onSelect?: (k: string | null) => void; selected?: string | null;
}) {
  const total = parts.reduce((s, p) => s + p.value, 0);
  const shown = parts.filter((p) => p.value > 0);
  return (
    <div>
      <div className="flex w-full gap-[2px] overflow-hidden rounded-full" style={{ height }} role="img"
        aria-label={`Distribuição: ${parts.map((p) => `${p.label} ${p.value}`).join(', ')}`}>
        {total === 0 ? <div className="h-full w-full rounded-full bg-panel-2" /> : shown.map((p) => (
          <button key={p.key} type="button" title={`${p.label}: ${p.value} (${Math.round((p.value / total) * 100)}%)`} aria-label={`${p.label}: ${p.value}`}
            onClick={() => onSelect?.(selected === p.key ? null : p.key)}
            className={clsx('h-full min-w-[6px] transition-opacity first:rounded-l-full last:rounded-r-full hover:brightness-110', selected && selected !== p.key && 'opacity-30')}
            style={{ width: `${(p.value / total) * 100}%`, background: p.color }} />
        ))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
        {parts.map((p) => (
          <li key={p.key}>
            <button type="button" onClick={() => onSelect?.(selected === p.key ? null : p.key)}
              className={clsx('inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 hover:bg-panel-2', selected && selected !== p.key && 'opacity-50')}>
              {p.icon ?? <span className="size-2.5 rounded-sm" style={{ background: p.color }} />}
              <span className="text-muted">{p.label}</span><b className="num text-fg">{p.value}</b>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Minicolunas (uma série, ex.: kickoffs por mês): valor no topo das colunas com dado, rótulo curto embaixo. */
export function MiniColumns({ data, height = 64, color = 'var(--teal)', highlight }: { data: { label: string; value: number; title?: string }[]; height?: number; color?: string; highlight?: number }) {
  const m = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex items-end gap-1.5" style={{ height: height + 30 }} role="img" aria-label={data.map((d) => `${d.title ?? d.label}: ${d.value}`).join(', ')}>
      {data.map((d, i) => (
        <div key={i} className="flex flex-1 flex-col items-center justify-end gap-1" title={`${d.title ?? d.label}: ${d.value}`}>
          {d.value ? <span className="num text-[10px] font-semibold text-fg">{d.value}</span> : null}
          <span className="w-full max-w-5 rounded-t-[4px]" style={{ height: d.value ? Math.max(4, (d.value / m) * height) : 2, background: d.value ? color : 'var(--grid)', opacity: highlight != null && i !== highlight ? 0.55 : 1 }} />
          <span className={clsx('text-[10px] leading-none', highlight === i ? 'font-semibold text-fg' : 'text-muted')}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

export interface ColumnSeries { key: string; label: string; color: string }

/** Largura do contêiner (px), acompanhando o redimensionamento: o gráfico desenha em 1:1, sem esticar texto e marcas. */
function useWidth(initial = 640) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(initial);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => { const v = Math.round(e!.contentRect.width); if (v > 0) setW(v); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/**
 * Colunas agrupadas no tempo (2 ou 3 séries por período, ex.: recebidos × entregues): eixo de 0 a um teto redondo em
 * números inteiros, grade discreta, colunas finas com a ponta arredondada e 2 px entre elas, legenda com o total de cada
 * série e dica com todas as séries do período (mouse ou foco).
 */
export function GroupedColumns({ data, series, height = 220, xFmt = (x: string) => x, tipFmt = (x: string) => x }: {
  data: { x: string; values: number[] }[]; series: ColumnSeries[]; height?: number; xFmt?: (x: string) => string; tipFmt?: (x: string) => string;
}) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const [box, W] = useWidth();
  if (!data.length) return <div className="grid h-32 place-items-center text-sm text-muted">Sem dados para o gráfico.</div>;
  const ticks = niceTicks(Math.max(4, ...data.flatMap((d) => d.values)));
  const max = ticks.at(-1)!;
  const pad = { l: axisLeft(ticks.map(String)), r: 8, t: 12, b: 26 };
  const band = (W - pad.l - pad.r) / data.length;
  const n = series.length;
  const barW = Math.max(2, Math.min(14, (band * 0.6 - (n - 1) * 2) / n));
  const groupW = n * barW + (n - 1) * 2;
  const y = (v: number) => pad.t + (1 - v / max) * (height - pad.t - pad.b);
  const base = y(0);
  const cx = (i: number) => pad.l + band * i + band / 2;
  // Coluna com só as pontas de cima arredondadas (4 px), presa à linha de base.
  const col = (x: number, h: number) => {
    const r = Math.min(4, barW / 2, h);
    return `M${x},${base} V${base - h + r} Q${x},${base - h} ${x + r},${base - h} H${x + barW - r} Q${x + barW},${base - h} ${x + barW},${base - h + r} V${base} Z`;
  };
  // Rótulos do eixo do tempo: um a cada tantos períodos, para caberem (uns 56 px cada).
  const labelEvery = Math.max(1, Math.ceil(56 / band));
  const totals = series.map((_, k) => data.reduce((s, d) => s + (d.values[k] ?? 0), 0));
  return (
    <div className="relative" ref={box}>
      <div className="mb-2 flex flex-wrap gap-4 text-xs text-muted">
        {series.map((s, k) => (
          <span key={s.key} className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-sm" style={{ background: s.color }} />{s.label} <b className="num text-fg">{totals[k]}</b></span>
        ))}
      </div>
      <svg width={W} height={height} viewBox={`0 0 ${W} ${height}`} className="block max-w-full" role="img" aria-labelledby={`${id}-t`} onPointerLeave={() => setHover(null)}>
        <title id={`${id}-t`}>{`${series.map((s, k) => `${s.label}: ${totals[k]}`).join(' · ')} (${tipFmt(data[0]!.x)} a ${tipFmt(data.at(-1)!.x)})`}</title>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke={t === 0 ? 'var(--axis)' : 'var(--grid)'} strokeWidth="1" />
            <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill="var(--muted)" className="num">{t}</text>
          </g>
        ))}
        {data.map((d, i) => (
          <g key={d.x}>
            {hover === i ? <rect x={pad.l + band * i} y={pad.t} width={band} height={base - pad.t} fill="var(--panel-2)" /> : null}
            {series.map((s, k) => {
              const v = d.values[k] ?? 0;
              return v > 0 ? <path key={s.key} d={col(cx(i) - groupW / 2 + k * (barW + 2), base - y(v))} fill={s.color} /> : null;
            })}
            {i % labelEvery === 0
              ? <text x={cx(i)} y={height - 8} textAnchor="middle" fontSize="10" fill="var(--muted)">{xFmt(d.x)}</text> : null}
            {/* Área de toque maior que a coluna: o período inteiro. */}
            <rect x={pad.l + band * i} y={pad.t} width={band} height={height - pad.t} fill="transparent" tabIndex={0} role="button"
              aria-label={`${tipFmt(d.x)}: ${series.map((s, k) => `${s.label} ${d.values[k] ?? 0}`).join(', ')}`}
              className="outline-none" onPointerEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} />
          </g>
        ))}
      </svg>
      {hover != null ? (
        <div role="tooltip" className="pointer-events-none absolute top-6 z-20 -translate-x-1/2 whitespace-nowrap rounded-md border border-line bg-panel px-2.5 py-1.5 text-xs shadow-lg"
          style={{ left: Math.min(W - 70, Math.max(70, cx(hover))) }}>
          <div className="mb-1 text-muted">{tipFmt(data[hover]!.x)}</div>
          {series.map((s, k) => (
            <div key={s.key} className="flex items-center gap-2"><span className="size-2 rounded-sm" style={{ background: s.color }} /><b className="num">{data[hover]!.values[k] ?? 0}</b><span className="text-muted">{s.label}</span></div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
